// Refatorado para remover 'any' e introspecção interna do Zod

import { z, ZodObject } from "zod";
import type { ZodRawShape } from "zod";
import type { Field, FormConfig, TabFieldsGroup } from "./Form";
import type { FormField } from "./FormFields";
import { convertScreensApiToFormConfig } from "./convertScreensApiToFormConfig";

// ================== Tipos ==================

export type RemoteValidatorHandler = (
  field: Field,
  value: string | boolean | File[] | string[] | undefined,
  formData: Record<string, unknown>
) => Promise<{ valid: boolean; message?: string } | undefined> | undefined;

export type OptionsNormalizer = (
  options: ReadonlyArray<{ label: string; value: string; [k: string]: unknown }>,
  field: FormField,
  formData: Record<string, unknown>
) => ReadonlyArray<{ label: string; value: string; [k: string]: unknown }> | Promise<ReadonlyArray<{ label: string; value: string; [k: string]: unknown }>>;

export interface FieldRule {
  field: string;
  visibleIf?: (formData: Record<string, unknown>) => boolean;
  enabledIf?: (formData: Record<string, unknown>) => boolean;
  readonlyIf?: (formData: Record<string, unknown>) => boolean;
}

export interface FieldDependencyRule {
  field: string;
  dependsOn: string;
  condition: (value: unknown) => boolean;
  requiredMessage?: string;
}

type FieldMapper = (valleField: unknown) => Partial<FormField> | Promise<Partial<FormField>>;

type ZodFieldShape = Record<string, z.ZodType<unknown>>;

export interface BuiltSchema {
  schema: z.ZodObject<ZodFieldShape>;
  visibilityMap: Record<string, (data: Record<string, unknown>) => boolean | undefined>;
}

// ================== Globals ==================

const remoteValidatorHandlers: RemoteValidatorHandler[] = [];
let globalOptionsNormalizer: OptionsNormalizer | undefined;
let debugWarnings: string[] = [];
const customFieldMappers: Record<string, FieldMapper> = {};

// ================== Registro / Config ==================

export function registerRemoteValidatorHandler(handler: RemoteValidatorHandler) {
  remoteValidatorHandlers.push(handler);
}

export function setOptionsNormalizer(normalizer: OptionsNormalizer) {
  globalOptionsNormalizer = normalizer;
}

export async function normalizeOptions(
  options: ReadonlyArray<{ label: string; value: string; [k: string]: unknown }> | undefined,
  field: FormField,
  formData: Record<string, unknown>
): Promise<ReadonlyArray<{ label: string; value: string; [k: string]: unknown }>> {
  if (!options) return [];
  if (globalOptionsNormalizer) {
    return await globalOptionsNormalizer(options, field, formData);
  }
  return options;
}

export function registerFieldMapper(type: string, mapper: FieldMapper) {
  customFieldMappers[type] = mapper;
}

// ================== Screens API Conversion ==================

export async function convertScreensApiToFormConfigWithCustomMappers(
  valleJson: Parameters<typeof convertScreensApiToFormConfig>[0]
): Promise<FormConfig> {
  debugWarnings = [];
  const config = convertScreensApiToFormConfig(valleJson);

  const mapField = async (field: FormField): Promise<FormField> => {
    if (field?.type && customFieldMappers[field.type]) {
      try {
        const mapped = await customFieldMappers[field.type](field);
        return { ...field, ...mapped };
      } catch (e) {
        debugWarnings.push(
          `Erro ao mapear campo "${field.id}" (${field.type}): ${e instanceof Error ? e.message : String(e)}`
        );
        return field;
      }
    } else if (field?.type && !customFieldMappers[field.type]) {
      debugWarnings.push(
        `Campo "${field.id}" (${field.type}) sem mapeador customizado.`
      );
    }
    return field;
  };

  const mappedFields = await Promise.all(
    (config.fields || []).map(async (group) => {
      if (Array.isArray(group)) {
        return Promise.all(group.map(mapField));
      }
      const tab = group as TabFieldsGroup;
      if (Array.isArray(tab.fields)) {
        return {
          ...tab,
            fields: await Promise.all(tab.fields.map(mapField)),
        };
      }
      return group;
    })
  );

  return { ...config, fields: mappedFields };
}

export function zodSchemaFromScreensApi(
  valleJson: Parameters<typeof convertScreensApiToFormConfig>[0]
): ZodObject<ZodRawShape> {
  const config = convertScreensApiToFormConfig(valleJson);
  const fields: Field[] = [];
  (config.fields || []).forEach((group) => {
    if (Array.isArray(group)) {
      fields.push(...group);
    } else if (group && Array.isArray((group as TabFieldsGroup).fields)) {
      fields.push(...(group as TabFieldsGroup).fields);
    }
  });
  return buildZodSchema(fields);
}

// ================== Regras / Dependências ==================

export function zodSchemaWithDependencies<T extends ZodRawShape>(
  baseSchema: ZodObject<T>,
  dependencies: ReadonlyArray<FieldDependencyRule>
): z.ZodEffects<ZodObject<T>> {
  return baseSchema.superRefine((data, ctx) => {
    dependencies.forEach((dep) => {
      if (dep.condition((data as Record<string, unknown>)[dep.dependsOn])) {
        const v = (data as Record<string, unknown>)[dep.field];
        if (
          v === undefined ||
          v === "" ||
          (Array.isArray(v) && v.length === 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [dep.field],
            message:
              dep.requiredMessage ||
              `Campo obrigatório devido a ${dep.dependsOn}`,
          });
        }
      }
    });
  });
}

export function applyFieldRules<F extends Field>(
  fields: ReadonlyArray<F>,
  formData: Record<string, unknown>,
  rules: ReadonlyArray<FieldRule>
): F[] {
  const fieldMap = new Map<string, F>(fields.map((f) => [f.id, { ...f }]));
  rules.forEach((rule) => {
    const field = fieldMap.get(rule.field);
    if (!field) return;
    if (rule.visibleIf) {
      (field as F & { visible?: boolean }).visible = rule.visibleIf(formData);
    }
    if (rule.enabledIf) {
      (field as F & { disabled?: boolean }).disabled = !rule.enabledIf(formData);
    }
    if (rule.readonlyIf) {
      (field as F & { readOnly?: boolean }).readOnly = rule.readonlyIf(formData);
    }
  });
  return Array.from(fieldMap.values());
}

// ================== I18n Helpers ==================

export function translateFieldMessages(
  backendMessages: { error_text?: string; helper_text?: string; [k: string]: string | undefined },
  i18nDict?: Record<string, string>
): { errorMessage?: string; helperText?: string } {
  const t = (msg?: string) =>
    msg && i18nDict && i18nDict[msg] ? i18nDict[msg] : msg;
  return {
    errorMessage: t(backendMessages.error_text),
    helperText: t(backendMessages.helper_text),
  };
}

export function useFieldMessageI18n(
  field: { errorMessage?: string; helperText?: string; error_text?: string; helper_text?: string },
  i18nDict?: Record<string, string>
) {
  const t = (msg?: string) =>
    msg && i18nDict && i18nDict[msg] ? i18nDict[msg] : msg;
  return {
    errorMessage: t(field.errorMessage || field.error_text),
    helperText: t(field.helperText || field.helper_text),
  };
}

export function autoI18nOptions(
  options: ReadonlyArray<{ label: string; value: string; [k: string]: unknown }>,
  i18nDict?: Record<string, string>
) {
  if (!i18nDict) return options;
  return options.map((opt) => ({
    ...opt,
    label: i18nDict[opt.label] || opt.label,
  }));
}

// ================== Zod Básico (legacy) ==================

export function buildZodSchema(fields: ReadonlyArray<Field>): ZodObject<ZodRawShape> {
  const shape: ZodRawShape = {};
  fields.forEach((field) => {
    if (field.type === "input" && field.required) {
      shape[field.id] = z
        .string()
        .min(1, { message: typeof field.errorMessage === "string" ? field.errorMessage : "Campo obrigatório" });
    } else if (field.type === "input") {
      shape[field.id] = z.string().optional();
    }
    if (field.type === "checkbox" && field.required) {
      const errorMsg =
        typeof field.errorMessage === "string"
          ? field.errorMessage
          : "Campo obrigatório";
      shape[field.id] = z
        .boolean()
        .refine((val): val is true => val === true, { message: errorMsg });
    }
    if (field.type === "upload" && field.required) {
      const errorMsg =
        typeof field.errorMessage === "string"
          ? field.errorMessage
          : "Arquivo obrigatório";
      shape[field.id] = z
        .array(z.instanceof(File))
        .min(1, { message: errorMsg });
    }
  });
  return z.object(shape);
}

// ================== Zod Avançado (Enhanced) ==================

function buildSingleFieldSchema(field: FormField): z.ZodType<unknown> {
  switch (field.type) {
    case "input": {
      const inputType = field.inputType || "text";
      if (inputType === "email") {
        return z.string().email({
          message: field.label ? `${field.label} inválido` : "E-mail inválido",
        });
      }
      if (inputType === "number") {
        return z
          .union([
            z.number(),
            z
              .string()
              .regex(/^-?\d+([.,]\d+)?$/)
              .transform((v) => Number(v.replace(",", "."))),
          ])
          .refine((v) => !Number.isNaN(v), {
            message: field.label ? `${field.label} inválido` : "Número inválido",
          });
      }
      return z.union([z.string(), z.number(), z.boolean()]).or(z.unknown());
    }
    case "select":
    case "radiobutton":
      return z.unknown();
    case "toggle":
      return z.boolean();
    case "upload":
      return z.union([z.array(z.instanceof(File)), z.instanceof(File)]).or(z.array(z.unknown())).or(z.unknown());
    case "date":
    case "time":
    case "datetime":
      return z
        .union([
          z.string(),
          z.preprocess(
            (v) => (v instanceof Date ? v : typeof v === "string" ? new Date(v) : v),
            z.date()
          ),
        ])
        .transform((v) => (v instanceof Date ? v.toISOString() : v));
    case "wysiwyg":
      return z.string().optional();
    default:
      return z.unknown();
  }
}

export function buildZodSchemaEnhanced(fields: ReadonlyArray<FormField>): BuiltSchema {
  const shape: ZodFieldShape = {};
  const visibilityMap: BuiltSchema["visibilityMap"] = {};

  fields.forEach((field) => {
    if (!field.id) return;
    let schema = buildSingleFieldSchema(field);

    // Acesso seguro à propriedade não declarada no tipo FormField (pattern)
    const pattern: string | undefined = (field as unknown as { pattern?: string }).pattern;
    if (pattern && field.type === "input") {
      try {
        const rx = new RegExp(pattern);
        if (schema instanceof z.ZodString) {
          schema = (schema as z.ZodString).regex(rx, {
            message: field.label ? `${field.label} inválido` : "Formato inválido",
          });
        }
      } catch {
        // ignore regex errors
      }
    }

    // Acesso seguro a maxLength (também pode não existir no tipo importado)
    const maxLength: number | undefined = (field as unknown as { maxLength?: number }).maxLength;
    if (maxLength && schema instanceof z.ZodString) {
      schema = schema.max(maxLength, {
        message: `Máximo ${maxLength} caracteres`,
      });
    }

    const isRequired: boolean | undefined = (field as unknown as { required?: boolean }).required;
    if (isRequired) {
      schema = schema.refine(
        (v) => {
          if (Array.isArray(v)) return v.length > 0;
            return v !== undefined && v !== null && v !== "";
        },
        {
          message: field.label
            ? `${field.label} é obrigatório`
            : "Campo obrigatório",
        }
      );
    }

    const visibleIfFn = (field as unknown as { visibleIf?: (d: Record<string, unknown>) => boolean }).visibleIf;
    if (typeof visibleIfFn === "function") {
      visibilityMap[field.id] = visibleIfFn;
    }

    shape[field.id] = schema;
  });

  return { schema: z.object(shape), visibilityMap };
}

export function parseWithVisibility(
  built: BuiltSchema,
  data: Record<string, unknown>
): Record<string, unknown> {
  const partialShape: ZodFieldShape = {};
  for (const [key, schema] of Object.entries(built.schema.shape)) {
    const isVisible = built.visibilityMap[key]
      ? built.visibilityMap[key](data)
      : true;
    if (isVisible) {
      partialShape[key] = schema;
    }
  }
  const effective = z.object(partialShape);
  return effective.parse(data);
}

// ================== Utilidades de Export Debug ==================

// Substitui defineProperty em exports por getters/setters explícitos tipados
export const getDebugWarnings = (): string[] => debugWarnings;
export const setDebugWarnings = (val: string[]): void => {
  debugWarnings = val;
};

// ================== Re-exports agrupados ==================

export {
  remoteValidatorHandlers, // se usado externamente
  customFieldMappers,
};